import { createContext, useContext } from 'react';
export { DEFAULT_PALETTE as PAL } from './brand-theme';

/**
 * Direction-1 ("Minimal Swiss") palette + chart colors.
 * Brand neutrals are locked from Phase 2.5b; chart colors stay distinct
 * so analytics dashboards remain readable.
 */
export interface Theme {
  bg: string;
  surface: string;
  surfaceHigh: string;
  card: string;
  border: string;
  text: string;
  sub: string;
  muted: string;
  // Semantic
  red: string;
  redSoft: string;
  gold: string;
  goldSoft: string;
  good: string;
  warn: string;
  bad: string;
  blue: string;
  blueSoft: string;
  purple: string;
  // Brand accent (for charts/buttons)
  accent: string;
  accentSoft: string;
}

export const DARK: Theme = {
  bg: '#0a0a0a',
  surface: '#111111',
  surfaceHigh: '#161616',
  card: '#111111',
  border: '#1f1f1f',
  text: '#fafafa',
  sub: '#a1a1a1',
  muted: '#8c8c8c',
  red: '#ef4444',
  redSoft: 'rgba(239,68,68,0.12)',
  gold: '#f59e0b',
  goldSoft: 'rgba(245,158,11,0.10)',
  good: '#22c55e',
  warn: '#f59e0b',
  bad: '#ef4444',
  blue: '#2563eb',
  blueSoft: 'rgba(37,99,235,0.12)',
  purple: '#a855f7',
  accent: '#2563eb',
  accentSoft: 'rgba(37,99,235,0.12)',
};

export const LIGHT: Theme = {
  bg: '#fafafa',
  surface: '#ffffff',
  surfaceHigh: '#f4f4f5',
  card: '#ffffff',
  border: '#e4e4e7',
  text: '#0a0a0a',
  sub: '#52525b',
  muted: '#6c6c6c',
  red: '#dc2626',
  redSoft: 'rgba(220,38,38,0.10)',
  gold: '#d97706',
  goldSoft: 'rgba(217,119,6,0.10)',
  good: '#16a34a',
  warn: '#d97706',
  bad: '#dc2626',
  blue: '#1d4ed8',
  blueSoft: 'rgba(29,78,216,0.10)',
  purple: '#9333ea',
  accent: '#1d4ed8',
  accentSoft: 'rgba(29,78,216,0.10)',
};

export const ThemeContext = createContext<Theme>(DARK);
export const useTheme = (): Theme => useContext(ThemeContext);

/**
 * @deprecated Use metricColor from './brand-theme'. Retained only for legacy MG components.
 */
export const cplColor = (cpl: number, theme?: Theme): string => {
  const t = theme ?? DARK;
  return cpl <= 0 ? t.muted : cpl < 110 ? t.good : cpl < 160 ? t.warn : t.bad;
};

export const fmt = {
  $: (v: number) =>
    `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
  $$: (v: number) =>
    `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
  N: (v: number) =>
    v >= 1e6
      ? `${(v / 1e6).toFixed(1)}M`
      : v >= 1000
        ? `${(v / 1000).toFixed(0)}k`
        : String(Math.round(v)),
  I: (v: number) => Math.round(v).toLocaleString('en-US'),
  P: (v: number) => `${Number(v).toFixed(2)}%`,
  pct: (v: number | null) => (v === null ? '—' : `${v > 0 ? '+' : ''}${v}%`),
};
